<template>
    <StaticSpinner v-if="!loaded" />
    <div v-else-if="knockoutStages.length > 0" class="knockouts">
        <Swiper
            v-bind:slidesPerView="KNOCKOUT_SWIPER_SLIDES_PER_VIEW"
            v-bind:autoHeight="true"
            class="knockouts_swiper"
            v-on:swiper="onSwiper"
            v-on:slideChangeTransitionEnd="onSlideChange"
        >
            <SwiperSlide
                v-for="stage in knockoutStagesData.knockoutStagesDisplay"
                v-bind:key="stage.stage"
                class="knockouts_round"
            >
                <KnockoutBracket v-bind:knockoutEvent="stage.matchList" />

                <div v-if="stage.stage === TournamentStage.Final" class="knockoutsSpecialRoundContainer">
                    <div class="knockoutsRoundTitle">
                        {{ thirdPlaceStageName }}
                    </div>
                    <KnockoutBracket v-bind:knockoutEvent="knockoutStagesData.thirdPlaceMatchList" />
                </div>
            </SwiperSlide>
            <SwiperSlide v-bind:key="'knockout-blank'" class="knockouts_round knockouts_round--blank" />
        </Swiper>
    </div>
</template>

<script lang="ts" setup>
    import type { Swiper as SwiperType } from 'swiper';
    import { Swiper, SwiperSlide } from 'swiper/vue';
    import { computed, ref, toRefs, watch } from 'vue';
    import StaticSpinner from '@/components/common/StaticSpinner.vue';
    import KnockoutBracket from '@/components/tournamentCenter/KnockoutBracket.vue';
    import { KNOCKOUT_SWIPER_SLIDES_PER_VIEW } from '@/components/tournamentCenter/config';
    import { getKnockoutEventStatus, getKnockoutRunningEvent, getKnockoutStagesForSlides } from '@/components/tournamentCenter/tournamentCenterHelper';
    import { i18n } from '@/core/lib/i18n';
    import { TournamentStage } from '@/interface/enum';
    import type { IKnockoutStage, ITournamentEvent } from '@/interface/tournamentCenter';

    const props = defineProps<{
        knockoutStages: IKnockoutStage[];
        events: ITournamentEvent[];
        selectedStage: TournamentStage;
        loaded: boolean;
    }>();

    const emit = defineEmits<{
        'update:selectedKnockoutStage': [value: TournamentStage];
    }>();

    const { knockoutStages, events, selectedStage } = toRefs(props);

    const swiperRef = ref<SwiperType | null>(null);

    const stages = computed(() => getKnockoutStagesForSlides(knockoutStages.value));

    watch([selectedStage, swiperRef], () => {
        const index = stages.value.indexOf(selectedStage.value);
        if (index >= 0 && swiperRef.value) {
            swiperRef.value.slideTo(index);
        }
    });

    const thirdPlaceStageName = computed(() => i18n.t('tournament_center_knockout_3rd_place'));

    const knockoutStagesData = computed(() => {
        const allKnockoutStages = knockoutStages.value.map(knockoutStage => ({
            stage: knockoutStage.stage,
            matchList: knockoutStage.eventList.map(event => ({
                ...event,
                status: getKnockoutEventStatus(event, events.value),
                runningEvent: getKnockoutRunningEvent(event, events.value),
            })),
        }));

        const knockoutStagesDisplay = allKnockoutStages
            .filter(knockoutStage => knockoutStage.stage !== TournamentStage.ThirdPlace);

        const thirdPlaceStage = allKnockoutStages.find(knockoutStage => knockoutStage.stage === TournamentStage.ThirdPlace);

        return {
            knockoutStagesDisplay,
            thirdPlaceMatchList: thirdPlaceStage?.matchList ?? [],
        };
    });

    function onSwiper(swiper: SwiperType) {
        swiperRef.value = swiper;
    }

    function onSlideChange(swiper: SwiperType) {
        swiper.updateAutoHeight(0);
        const realIndex = Math.min(swiper.activeIndex, stages.value.length - 1);
        const stage = stages.value[realIndex];
        if (stage !== undefined) {
            emit('update:selectedKnockoutStage', stage);
        }
    }
</script>

<style lang="scss" scoped>
    @import "@/components/tournamentCenter/KnockoutBracket";
</style>

<style lang="scss">
    @import "swiper/css";

    .knockouts_swiper {
        width: 100%;
    }
</style>
